export interface SitemapItem {
  path: string;
  title: string;
  category: string;
  isProtected?: boolean;
  isAdmin?: boolean;
  description?: string;
}

// Route metadata for better categorization and descriptions
const routeMetadata: Record<string, { title: string; category: string; description?: string; isProtected?: boolean; isAdmin?: boolean }> = {
  '/': { title: 'Home', category: 'Main Pages', description: 'Welcome to SaunaDip' },
  '/directory': { title: 'Sauna Directory', category: 'Main Pages', description: 'Browse and discover saunas' },
  '/pricing': { title: 'Pricing', category: 'Main Pages', description: 'Subscription plans and pricing' },
  '/blog': { title: 'Blog', category: 'Content', description: 'Latest articles and news' },
  '/about': { title: 'About', category: 'Main Pages', description: 'Learn about SaunaDip' },
  '/contact': { title: 'Contact', category: 'Main Pages', description: 'Get in touch with us' },
  '/project': { title: 'Project', category: 'Main Pages', description: 'Project information' },
  '/how-it-works': { title: 'How It Works', category: 'Main Pages', description: 'How SaunaDip works' },
  '/why-kit': { title: 'Why Kit', category: 'Main Pages', description: 'Why choose SaunaDip' },
  '/your-help': { title: 'Your Help', category: 'Main Pages', description: 'How you can help' },
  '/help-book': { title: 'Help Book', category: 'Resources', description: 'Help and documentation' },
  '/content-review': { title: 'Content Review', category: 'Resources', description: 'Review content' },
  
  // Authentication pages
  '/login': { title: 'Login', category: 'Authentication', description: 'Log in to your account' },
  '/register': { title: 'Register', category: 'Authentication', description: 'Create a new account' },
  '/signup/guest': { title: 'Guest Signup', category: 'Authentication', description: 'Sign up as a guest' },
  '/signup/operator': { title: 'Operator Signup', category: 'Authentication', description: 'Sign up as an operator' },
  '/forgot-password': { title: 'Forgot Password', category: 'Authentication', description: 'Reset your password' },
  '/reset-password': { title: 'Reset Password', category: 'Authentication', description: 'Set new password' },
  '/verify-email': { title: 'Verify Email', category: 'Authentication', description: 'Email verification' },
  '/choose-signup': { title: 'Choose Signup Type', category: 'Authentication', description: 'Select account type' },
  '/multi-step-signup-demo': { title: 'Multi-Step Signup Demo', category: 'Demo', description: 'Demo of multi-step signup' },
  '/multi-step-signup-with-role-demo': { title: 'Multi-Step Signup with Role Demo', category: 'Demo', description: 'Demo of role-based signup' },
  
  // Protected pages
  '/operator-onboarding': { title: 'Operator Onboarding', category: 'Protected', isProtected: true, description: 'Complete operator setup' },
  '/profile': { title: 'Profile', category: 'Protected', isProtected: true, description: 'Manage your profile' },
  
  // Admin pages
  '/admin': { title: 'Admin Dashboard', category: 'Admin', isProtected: true, isAdmin: true, description: 'Admin dashboard' },
  '/admin-landing': { title: 'Admin Landing', category: 'Admin', isProtected: true, isAdmin: true, description: 'Admin landing page' },
  '/admin/blogs': { title: 'Blog Management', category: 'Admin', isProtected: true, isAdmin: true, description: 'Manage blog posts' },
  '/admin/blogs/new': { title: 'New Blog Post', category: 'Admin', isProtected: true, isAdmin: true, description: 'Create new blog post' },
  '/admin/operator-admin-simple': { title: 'Operator Admin Simple', category: 'Admin', isProtected: true, isAdmin: true, description: 'Simple operator admin' },
  '/admin/users': { title: 'User Management', category: 'Admin', isProtected: true, isAdmin: true, description: 'Manage users' },
  '/admin/operators': { title: 'Operator Management', category: 'Admin', isProtected: true, isAdmin: true, description: 'Manage operators' },
  '/admin/locations': { title: 'Location Management', category: 'Admin', isProtected: true, isAdmin: true, description: 'Manage locations' },
  '/admin/services': { title: 'Service Management', category: 'Admin', isProtected: true, isAdmin: true, description: 'Manage services' },
  '/admin/memberships': { title: 'Membership Management', category: 'Admin', isProtected: true, isAdmin: true, description: 'Manage memberships' },
  '/admin/bookings': { title: 'Booking Management', category: 'Admin', isProtected: true, isAdmin: true, description: 'Manage bookings' },
  '/admin/reviews': { title: 'Review Management', category: 'Admin', isProtected: true, isAdmin: true, description: 'Manage reviews' },
  '/admin/blog': { title: 'Blog Management', category: 'Admin', isProtected: true, isAdmin: true, description: 'Blog management' },
  '/admin/support': { title: 'Support Management', category: 'Admin', isProtected: true, isAdmin: true, description: 'Manage support' },
  '/admin/settings': { title: 'Settings', category: 'Admin', isProtected: true, isAdmin: true, description: 'Admin settings' },
  
  // Subscription pages
  '/subscription': { title: 'Subscription', category: 'Subscription', description: 'Manage your subscription' },
  '/subscription-success': { title: 'Subscription Success', category: 'Subscription', description: 'Subscription confirmed' },
  
  // Contact pages
  '/contact-thank-you': { title: 'Contact Thank You', category: 'Contact', description: 'Thank you for contacting us' },
  
  // Legal pages
  '/terms-and-conditions': { title: 'Terms and Conditions', category: 'Legal', description: 'Terms of service' },
  '/privacy-policy': { title: 'Privacy Policy', category: 'Legal', description: 'Privacy policy' },
  '/cookie-policy': { title: 'Cookie Policy', category: 'Legal', description: 'Cookie usage policy' },
  '/acceptable-use-policy': { title: 'Acceptable Use Policy', category: 'Legal', description: 'Acceptable use guidelines' },
  '/terms-of-use': { title: 'Terms of Use', category: 'Legal', description: 'Terms of use' },
  
  // Dynamic routes (with placeholders)
  '/sauna/:id': { title: 'Sauna Details', category: 'Dynamic', description: 'Individual sauna information' },
  '/blog/:slug': { title: 'Blog Post', category: 'Dynamic', description: 'Individual blog post' },
  '/blog/category/:categorySlug': { title: 'Blog Category', category: 'Dynamic', description: 'Blog posts by category' },
  '/admin/blogs/edit/:id': { title: 'Edit Blog Post', category: 'Admin', isProtected: true, isAdmin: true, description: 'Edit existing blog post' },
  
  // Temporary/Demo pages
  '/tempwizardpage': { title: 'Temp Wizard Page', category: 'Demo', description: 'Temporary wizard page' },
  '/tempsignup': { title: 'Temp Signup Page', category: 'Demo', description: 'Temporary signup page' },
  '/TempComingSoonLandingPage': { title: 'Temp Coming Soon Landing', category: 'Demo', description: 'Temporary coming soon page' },
  '/TempMarketingLandingPage': { title: 'Temp Marketing Landing', category: 'Demo', description: 'Temporary marketing page' },
  
  // Other pages
  '/operator-survey': { title: 'Operator Survey', category: 'Main Pages', description: 'Operator survey' },
  '/coming-soon': { title: 'Coming Soon', category: 'Main Pages', description: 'Coming soon page' },
  '/social-assets': { title: 'Social Assets', category: 'Resources', description: 'Social media assets' },
  '/design-system': { title: 'Design System', category: 'Resources', description: 'Design system documentation' },
  '/dev-system': { title: 'Dev System', category: 'Resources', description: 'Development system' },
  '/operator-admin': { title: 'Operator Admin', category: 'Admin', isProtected: true, isAdmin: true, description: 'Operator administration' },
  '/operator-admin-simple': { title: 'Operator Admin Simple', category: 'Admin', isProtected: true, isAdmin: true, description: 'Simple operator admin' },
  '/sitemap': { title: 'Sitemap', category: 'Resources', description: 'Site navigation' },
  '/maintenance': { title: 'Maintenance', category: 'System', description: 'Site maintenance page' },
};

// Routes to exclude from sitemap (only truly private/admin/demo pages)
const excludedRoutes = [
  // System pages
  '/maintenance',
  '/sitemap',
  
  // Temporary/Demo pages
  '/tempwizardpage',
  '/tempsignup',
  '/TempComingSoonLandingPage',
  '/TempMarketingLandingPage',
  '/multi-step-signup-demo',
  '/multi-step-signup-with-role-demo',
  '/content-review',
  '/help-book',
  '/your-help',
  '/why-kit',
  '/project',
  '/about',
  '/operator-survey',
  '/social-assets',
  '/design-system',
  '/dev-system',
  '/coming-soon',
  
  // Protected pages
  '/operator-onboarding',
  '/profile',
  '/verify-email',
  '/reset-password',
  '/forgot-password',
  '/choose-signup',
  '/signup/operator',
  '/contact-thank-you',
  '/subscription-success',
  
  // Admin pages
  '/admin',
  '/admin-landing',
  '/admin/blogs',
  '/admin/blogs/new',
  '/admin/blogs/edit/:id',
  '/admin/users',
  '/admin/operators',
  '/admin/locations',
  '/admin/services',
  '/admin/memberships',
  '/admin/bookings',
  '/admin/reviews',
  '/admin/blog',
  '/admin/support',
  '/admin/settings',
  '/admin/operator-admin-simple',
  '/admin/operator-admin',
  '/operator-admin',
  '/operator-admin-simple',
  
  // Dynamic routes
  '/sauna/:id',
  '/blog/:slug',
  '/blog/category/:categorySlug',
];

// Read toggle from env
const SHOW_ALL = import.meta.env.VITE_SITEMAP_SHOW_ALL === 'true';

export function generateSitemap(): SitemapItem[] {
  const sitemapItems: SitemapItem[] = [];
  
  // Add routes from metadata
  Object.entries(routeMetadata).forEach(([path, metadata]) => {
    // Only skip excluded routes if SHOW_ALL is false
    if (!SHOW_ALL && excludedRoutes.includes(path)) {
      return;
    }
    sitemapItems.push({
      path,
      title: metadata.title,
      category: metadata.category,
      description: metadata.description,
      isProtected: metadata.isProtected,
      isAdmin: metadata.isAdmin,
    });
  });
  
  // Sort by category, then by title
  return sitemapItems.sort((a, b) => {
    if (a.category !== b.category) {
      return a.category.localeCompare(b.category);
    }
    return a.title.localeCompare(b.title);
  });
}

export function getSitemapCategories(): string[] {
  const categories = new Set<string>();
  generateSitemap().forEach(item => categories.add(item.category));
  return Array.from(categories).sort();
}

export function getSitemapByCategory(): Record<string, SitemapItem[]> {
  const items = generateSitemap();
  const grouped: Record<string, SitemapItem[]> = {};
  
  items.forEach(item => {
    if (!grouped[item.category]) {
      grouped[item.category] = [];
    }
    grouped[item.category].push(item);
  });
  
  return grouped;
} 